import { useEffect, useState } from 'react';
import { Input, Select, Typography } from 'antd';
import parsePhoneNumber from 'libphonenumber-js/mobile';

import styles from './phoneInput.module.scss';

import { defaultCountries, CountryNumber } from './countries';
import { useTranslation } from 'react-i18next';
import { getCountriesCode } from './countriesService';

const { Option } = Select;

interface InputProps {
	userDialCode?: string;
	userPhoneNumber?: string;
	onChange?: (data: PhoneInputReturn) => void;
	disabled?: boolean;
	fullWidth?: boolean;
	label?: string;
	labeled?: boolean; // to show or hide label
	value?: string;
	noAutoComplete?: boolean;
}

export interface PhoneInputReturn {
	dialCode: string;
	number: string;
	countryCode: string;
	isValid: boolean;
}

function PhoneInputComponent({ onChange, userDialCode, userPhoneNumber, fullWidth, disabled, label, labeled = true, value, noAutoComplete }: InputProps) {
	const { t } = useTranslation();

	const [countries, setCountries] = useState<CountryNumber[]>(defaultCountries);
	const [selectedCountry, setSelectedCountry] = useState<CountryNumber>(defaultCountries[0]);

	const [dialCode, setDialCode] = useState<string>(selectedCountry.dialCode);
	const [number, setNumber] = useState<string>(value || '');

	const onGetCountriesCodes = async () => {
		try {
			const codes = await getCountriesCode();
			setCountries(codes);
		} catch (error) {
		}
	};

	useEffect(() => {
		onGetCountriesCodes();
	}, []);

	useEffect(() => {
		if (userDialCode) {
			const country = countries.find((el) => el.dialCode === '+' + userDialCode) || selectedCountry;
			setSelectedCountry(country);
			setDialCode('+' + userDialCode);
		}
		if (userPhoneNumber) setNumber(userPhoneNumber);
		if (!userPhoneNumber && !userDialCode) {
			setNumber('');
			setDialCode(selectedCountry.dialCode);
		}
	}, [userDialCode, userPhoneNumber, countries]);

	const onChangeCountry = (selectedCountryCode: string) => {
		const country = countries.find((el) => el.isoCode === selectedCountryCode) || selectedCountry;
		setSelectedCountry(country);
		setDialCode(country.dialCode);
		onChange &&
			onChange({
				dialCode: country.dialCode.replace('+', ''),
				number: number,
				countryCode: country.isoCode,
				isValid: checkPhoneValidation(country.dialCode, number),
			});
	};

	const onPhoneInput = (e: any) => {
		e.target.value = e.target.value.replace(/[^0-9]/g, '');
		setNumber(e.target.value);
		onChange &&
			onChange({
				dialCode: dialCode.replace('+', ''),
				number: e.target.value,
				countryCode: selectedCountry.isoCode,
				isValid: checkPhoneValidation(dialCode, e.target.value),
			});
	};

	const onPhoneKeyDown = (e: any) => {
		if (e.which === 38 || e.which === 40) {
			e.preventDefault();
		}
	};

	const selectBefore = (
		<Select size="middle" value={selectedCountry.isoCode} className={styles.select_element} onChange={onChangeCountry} disabled={disabled}>
			{countries.map((countryCode) => (
				<Option value={countryCode.isoCode} key={countryCode.isoCode} className="mb-1">
					<span style={{ direction: 'ltr', display: 'block', fontWeight: 500, color: '#555' }}>{countryCode.dialCode}</span>
				</Option>
			))}
		</Select>
	);

	const checkPhoneValidation = (code: string, number: string) => {
		const phoneNumber = parsePhoneNumber(code + number);
		return phoneNumber && phoneNumber.isValid() ? true : false;
	};

	return (
		<>
			{labeled && (
				<Typography.Text className="d-block" style={{ marginBottom: 5, fontSize: 15 }}>
					{label || t('COMMON.PHONE_NUMBER')}
				</Typography.Text>
			)}

			<Input
				addonBefore={selectBefore}
				className={`${styles.phoneInput} phoneInput`}
				onInput={onPhoneInput}
				onKeyDown={onPhoneKeyDown}
				type="number"
				autoComplete={noAutoComplete ? 'new-password' : undefined}
				disabled={disabled}
				value={number}
				style={{ direction: 'ltr', maxWidth: '100%', width: fullWidth ? '100%' : 'var(--inputWidth)' }}
			/>
		</>
	);
}

export default PhoneInputComponent;
